/**
 * Pedir atención y ver el estado del servicio.
 *
 * El botón de emergencia está primero y se distingue: cuando alguien lo
 * necesita, no está para leer.
 */

import { useState } from 'react';
import {
  dateLabel, REQUEST_CATEGORY_LABEL, REQUEST_STATUS_LABEL, REQUEST_STATUS_TONE,
  type RequestCategory, type RequestStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Badge, Button, Empty, Field, Modal, Note } from '@/components/ui';
import { Icon, type IconName } from '@/components/common/NavIcons';

interface Unit { id: string; unit_code: string; name: string | null }

interface Request {
  id: string; category: RequestCategory; note: string | null; priority: string;
  status: RequestStatus; created_at: string; resolved_at: string | null; unit_code: string;
}

/* «Limpieza adicional», «Solicitud especial» y «Otro» se sacaron de acá
   (dueño, 2-sep-2026): la adicional ya la cubre Emergencia — no es una
   opción aparte — y las otras dos «no tienen sentido», no aportan sobre lo
   que ya cubren las demás categorías. Los valores siguen existiendo en el
   dominio por si algo viejo los usa; esta pantalla simplemente ya no ofrece
   esos botones. */
const CATEGORIES: RequestCategory[] = ['EMERGENCY', 'DEEP_CLEANING', 'SUPPLIES'];

export function PortalServices() {
  const home = useApi<{ units: Unit[] }>('/portal/home');
  const requests = useApi<{ requests: Request[] }>('/portal/requests');
  const [asking, setAsking] = useState<RequestCategory | null>(null);

  const unit = home.data?.units[0];

  return (
    <>
      <section className="msec">
        <div className="msec__title">Solicitar</div>
        {/* El aviso va ANTES de tocar nada: si aparecía recién dentro del
            modal (como pasaba antes), la persona ya había elegido una
            categoría creyendo que iba a funcionar. */}
        {!unit && home.data && (
          <Note tone="warn">Tu cuenta todavía no está asociada a un consultorio.</Note>
        )}
        {/* Los accesos son `<button>` (abren un modal), y se visten con la
            MISMA regla que los enlaces de Inicio (`.quick a, .quick button`
            en base.css). Antes llevaban 12 estilos inline que copiaban esa
            regla… y el de Emergencia igual no se pintaba rojo, porque
            `.is-urgent` solo alcanzaba a los `<a>`. */}
        {/* `.quick` por defecto trae 4 columnas fijas (Inicio, que sí tiene
            cuatro accesos) — acá quedan 3 desde que se sacaron «Limpieza
            adicional», «Solicitud especial» y «Otro» (dueño, 2-sep-2026), y
            sin el modificador la cuarta columna vacía corría las tarjetas a
            la izquierda en vez de repartirlas parejo (dueño, 3-sep-2026). */}
        <div className="quick quick--3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={cat === 'EMERGENCY' ? 'is-urgent' : ''}
              disabled={!unit}
              onClick={() => setAsking(cat)}
            >
              <span className="quick__ico">
                {/* Íconos de la familia, no glifos: «✦» y «▣» corren el riesgo
                    de emojificarse en el teléfono, como los demás que ya se
                    migraron. */}
                <Icon name={cat === 'EMERGENCY' ? 'incidencias' : cat === 'SUPPLIES' ? 'inventario' : 'servicios'} size={20} />
              </span>
              {REQUEST_CATEGORY_LABEL[cat]}
            </button>
          ))}
        </div>
      </section>

      <section className="msec">
        <div className="msec__title">Mis solicitudes</div>
        <Async query={requests} empty={(d) => d.requests.length === 0 && (
          <Empty icon="confirmar" title="No hay solicitudes activas" hint="Pedí lo que necesités desde arriba." />
        )}>
          {(data) => (
            /* A dos columnas en escritorio; en el teléfono la rejilla no
               existe y las tarjetas se apilan como siempre. */
            <div className="portal-grid2">
              {data.requests.map((r) => (
                <div key={r.id} className="mcard">
                  <div className="mrow" style={{ paddingTop: 0 }}>
                    <span>
                      <strong>{REQUEST_CATEGORY_LABEL[r.category]}</strong>
                      {r.note && (
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>{r.note}</span>
                      )}
                    </span>
                    <Badge tone={REQUEST_STATUS_TONE[r.status]}>{REQUEST_STATUS_LABEL[r.status]}</Badge>
                  </div>
                  <div className="mrow" style={{ borderBottom: 0, paddingBottom: 0 }}>
                    <span className="mrow__l">{dateLabel(r.created_at.slice(0, 10))}</span>
                    <span className="mrow__r small muted">{r.unit_code}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Async>
      </section>

      {/* Con el aviso de arriba y los botones deshabilitados sin consultorio,
          `asking` nunca puede quedar en `true` sin `unit`. */}
      {asking && unit && (
        <RequestModal category={asking} unit={unit} onClose={() => setAsking(null)} />
      )}
    </>
  );
}

/* Los insumos que Clean Blue repone. Son conocidos y son estos (CB-07): el
   cliente los marca en vez de redactarlos, con su ícono — una lista de
   casillas genéricas no ayudaba a escoger rápido (dueño, 2-sep-2026). */
const INSUMOS: { nombre: string; icon: IconName }[] = [
  { nombre: 'Toallas de papel', icon: 'insumo-toallas' },
  { nombre: 'Papel higiénico', icon: 'insumo-papel' },
  { nombre: 'Jabón de manos', icon: 'insumo-jabon' },
];

function RequestModal({ category, unit, onClose }: {
  category: RequestCategory; unit: Unit; onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [insumos, setInsumos] = useState<Set<string>>(() => new Set());

  /* Cada categoría pide lo SUYO, no un formulario genérico («parecen como un
     examen donde puedes poner lo que sea» — dueño, 29-ago-2026):
       · una emergencia no lleva descripción: el motivo no cambia la respuesta,
         que es salir ya. El modal entero ES la confirmación.
       · los insumos se marcan de una lista conocida, no se redactan.
       · las limpiezas (adicional/profunda) conservan el detalle opcional,
         porque ahí sí ayuda a llegar preparados. */
  const esEmergencia = category === 'EMERGENCY';
  const esInsumos = category === 'SUPPLIES';
  /* La profunda no se redacta: se confirma y listo, como la emergencia — es
     un trabajo grande, se agenda aparte, no algo que dependa de lo que
     alguien escriba acá (dueño, 2-sep-2026). */
  const esProfunda = category === 'DEEP_CLEANING';

  const alternarInsumo = (nombre: string) => setInsumos((prev) => {
    const s2 = new Set(prev);
    if (s2.has(nombre)) s2.delete(nombre); else s2.add(nombre);
    return s2;
  });

  const mutation = useApiMutation(
    () => api.post<{ duplicate: boolean }>('/portal/requests', {
      service_unit_id: unit.id,
      category,
      note: esEmergencia || esProfunda
        ? undefined
        : esInsumos
          ? [...insumos].join(', ')
          : (note || undefined),
      priority: esEmergencia ? 'URGENTE' : 'NORMAL',
    }),
    ['/portal/requests'],
  );

  /* El `Modal` del sistema (Escape, foco, impresión) en vez de una copia a
     mano del backdrop: el contenido y los botones son los mismos de antes. */
  return (
    <Modal
      title={REQUEST_CATEGORY_LABEL[category]}
      onClose={onClose}
      footer={mutation.isSuccess ? (
        <Button variant="primary" block onClick={onClose}>Cerrar</Button>
      ) : (
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={mutation.isPending || (esInsumos && insumos.size === 0)}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending
              ? 'Enviando…'
              : esEmergencia
                ? 'Sí, enviar la emergencia'
                : esInsumos
                  ? `Pedir ${insumos.size === 0 ? 'insumos' : insumos.size === 1 ? '1 insumo' : `${insumos.size} insumos`}`
                  : 'Enviar solicitud'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess && (
        <Note tone="ok">
          {mutation.data?.duplicate
            ? 'Ya teníamos tu solicitud de hace un rato. No creamos otra.'
            : 'Listo, Clean Blue fue notificado.'}
        </Note>
      )}

      {!mutation.isSuccess && esEmergencia && (
        <p>
          Sale el aviso de <strong>limpieza de emergencia</strong> para el
          consultorio {unit.unit_code} y el equipo va apenas lo reciba.
        </p>
      )}

      {!mutation.isSuccess && esProfunda && (
        <p>
          Sale el pedido de <strong>limpieza profunda</strong> para el
          consultorio {unit.unit_code} — es una por mes. Como es un trabajo
          más grande, se agenda fuera del horario de limpieza diaria (después
          de las 9 a. m.); Clean Blue te confirma la fecha.
        </p>
      )}

      {!mutation.isSuccess && esInsumos && (
        <>
          <p className="small muted mb-2">¿Qué hace falta en el consultorio {unit.unit_code}?</p>
          {/* Chips grandes con ícono, no una lista de casillas genérica —
              se elige por la forma, no solo por el nombre (dueño, 2-sep-2026). */}
          <div className="insumo-opciones" role="group" aria-label="Insumos que hacen falta">
            {INSUMOS.map(({ nombre, icon }) => {
              const on = insumos.has(nombre);
              return (
                <button
                  key={nombre}
                  type="button"
                  className={`insumo-opcion${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => alternarInsumo(nombre)}
                >
                  <span className="insumo-opcion__ico"><Icon name={icon} size={26} /></span>
                  {nombre}
                </button>
              );
            })}
          </div>
        </>
      )}

      {!mutation.isSuccess && !esEmergencia && !esProfunda && !esInsumos && (
        <>
          <p className="small muted mb-2">Consultorio {unit.unit_code}</p>
          <Field label="Motivo" hint="Opcional, pero ayuda a llegar preparados.">
            <textarea className="input" rows={3} maxLength={500} value={note}
              onChange={(e) => setNote(e.target.value)} />
          </Field>
        </>
      )}
    </Modal>
  );
}
